/**
 * The frame for the screens you can reach without a session.
 *
 * SEPARATE FROM app-shell.tsx BECAUSE THE SIDEBAR CANNOT BE MOUNTED, not merely
 * because it would look wrong. `SidebarMenuButton` reads `useSidebar()`, which
 * throws outside a `SidebarProvider` — so the nav, and the theme toggle that
 * lives in its footer, are unavailable on a login screen rather than omitted from
 * one. Anything that renders before a session exists renders here.
 *
 * NO NAV AND NO BREADCRUMB, and not because there was no room. There is nothing
 * to navigate to: a person with no session has exactly one thing to do on each of
 * these four screens, and a link to a screen that would bounce them straight back
 * to this one is furniture. The only links are the ones between the four, and
 * each page states its own.
 *
 * THE LAYOUT IS shadcn's login-03: a muted page, the product mark centred above
 * a single card, the card's own header centred. One shell, four screens — the
 * reset and invite pages inherit the look by construction, not by imitation.
 *
 * THE SPLASH IS HERE TOO, because it is the same surface at the moment before we
 * know which of the two shells applies. It deliberately says nothing and asks
 * nothing: `/auth/me` is one round trip, and a login form thrown up for that long
 * is a login form an already-signed-in person will start typing into.
 */
import type { ReactNode } from 'react'
import { MicIcon } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'

/** The product mark, so a bookmarked login page says which panel it belongs to. */
function Mark() {
  return (
    <div className="flex items-center gap-2 self-center">
      <div className="bg-primary text-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
        <MicIcon className="size-4" />
      </div>
      <span className="text-sm font-medium">Voice AI</span>
    </div>
  )
}

export function AuthShell({
  title,
  intro,
  children,
  footer,
}: {
  title: string
  /** One or two sentences saying what this screen is for. */
  intro?: ReactNode
  children: ReactNode
  /** The links out. Rendered under the card, in small type. */
  footer?: ReactNode
}) {
  return (
    // min-h-svh, not min-h-screen: on iOS Safari `100vh` includes the address
    // bar, so a centred card sits visibly low and the submit button can land
    // under the browser chrome.
    // bg-muted/50, not bg-muted: half-strength over the white body lands on the
    // lighter gray the login-03 reference actually shows, and in dark mode the
    // same blend stays a step above the page instead of vanishing into it.
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-muted/50 p-6 md:p-10">
      {/* max-w-sm on the whole stack, so the card, the heading and the footer
          links share one measure rather than three. */}
      <main className="flex w-full max-w-sm flex-col gap-6">
        <Mark />
        {/* shadow-sm: the house Card is ring-only; login-03's card floats on a
            soft shadow, and on a muted page that lift is most of the design. */}
        <Card className="shadow-sm">
          <CardHeader className="text-center">
            <CardTitle className="text-xl">{title}</CardTitle>
            {intro && <CardDescription>{intro}</CardDescription>}
          </CardHeader>
          <CardContent>{children}</CardContent>
        </Card>
        {footer && (
          <div className="text-center text-sm text-muted-foreground">{footer}</div>
        )}
      </main>
    </div>
  )
}

/**
 * The one render between mount and knowing whether there is a session.
 *
 * No spinner and no skeleton of the screen behind it. A skeleton would be a
 * promise about which screen is coming, and at this point in the load we do not
 * know: it is either the panel or the login form.
 */
export function AuthSplash() {
  return (
    <div className="flex min-h-svh items-center justify-center bg-muted/50 p-4">
      <p className="text-sm text-muted-foreground" role="status">
        Checking your session&hellip;
      </p>
    </div>
  )
}
